import dirtBikeHero from 'assets/images/psp/PSPHero/dirt-bike-hero.webp';

import bicycle from 'assets/images/psp/Bicycle/bicycle.webp';
import shutterstock from 'assets/images/psp/ATV/shutterstock.webp';
import carStorage from 'assets/images/psp/FeaturedContentImages/car-storage-guide.webp';
import motorcycle from 'assets/images/psp/FeaturedContentImages/motorcycle-storage.webp';

import dirtbike from 'assets/images/psp/dirtbike.svg';

import { storageCategories } from '../utils/getPSPLinks';

const faqs = [
	{
		question: 'How do I transport my dirt bike to storage?',
		answer: 'Most riders use pickup trucks with ramps or enclosed trailers for transport. Ensure your bike is properly secured and protected during transport. Some storage facilities offer move-in assistance or can recommend local transport services for riders without appropriate vehicles.',
	},
	{
		question: ' How do I prepare my dirt bike for storage?',
		answer:
			'Start by giving your bike a thorough cleaning to remove all dirt, mud, and debris that could trap moisture and cause corrosion over time. Then:\n\n' +
			'1. Change the oil\n' +
			'2. Fill your fuel tank\n' +
			'3. Check tire pressure and adjust as needed\n' +
			'4. Remove the battery\n' +
			'5. Clean and lubricate chains\n' +
			'6. Cover the motorcycle',
	},
	{
		question: 'Can I perform maintenance in my storage unit?',
		answer: 'Most facilities allow basic maintenance like oil changes, chain lubrication, and minor repairs. Check specific policies regarding fluid disposal and ventilation requirements. Avoid major repairs involving strong chemicals or creating a significant mess',
	},
	{
		question: 'What security features should I look for?',
		answer: "Essential security includes controlled access gates, individual unit locks, and surveillance cameras. Premium facilities offer additional features like motion sensors, on-site staff, and enhanced lighting. Your dirt bike's value should guide security level decisions.",
	},
	{
		question: 'How do I handle insurance for stored dirt bikes?',
		answer: "Review your homeowner's or renter's insurance to understand coverage limits for stored vehicles. Many riders purchase additional coverage through storage facilities or specialized motorcycle insurance policies for comprehensive protection.",
	},
];

const reviews = [
	{
		title: 'Very satisfied customer',
		stars: 5,
		text: 'Everyone in the office was very nice and helpful. They showed me the unit which was very nice, gave me the code and I was able to put my bike in it.',
		reviewerInitials: 'EM',
		link: 'https://www.trustpilot.com/reviews/62b348d79d448e2f5a51a9a6',
		customerName: 'ELLY MORRISSETTE',
		date: 'Jun 23, 2022',
	},
	{
		title: 'Safe and clean',
		stars: 5,
		text: 'I made a reservation online for one price with AAA, but the manager there gave me an even smaller unit at a lower price. It worked out very well for me. It is a gated facility with a security code for each customer. They also had locks. She walked me through how everything worked and was very kind. I rented a 3x5x8 just for suitcases, a few boxes and a bicycle. I recommend this storage place. I found them on selfstorage.com.',
		reviewerInitials: 'CS',
		link: 'https://www.trustpilot.com/reviews/5748b25e0000ff00095fd5aa',
		customerName: 'Customer Schulte',
		date: 'May 28, 2016',
	},
	{
		title: 'Best 1st Impression',
		stars: 5,
		text: 'I have had multiple units in the past but this was by far the best. The rep was able to search for a location fitting my needs and booked me an appointment the same day. I showed up and the associates at Smart Shop Self Storage in Huntington Beach were extremely nice and helpful. I was able to get the perfect unit to store my motorcycle, and it was very affordable! Best first impression I’ve had to date.',
		reviewerInitials: 'AH',
		link: 'https://www.trustpilot.com/reviews/5daf9ec16048580988adc0f0',
		customerName: 'Anthony Hainsworth',
		date: 'Oct 23, 2019',
	},
	{
		title: 'This site is awesome!',
		stars: 5,
		text: 'SpareFoot appeared in a search for cheap storage options. With SpareFoot, I was able to quickly find a new facility, closer to home, with the same size unit for $200 less than others! This site was right on time—I had just signed a lease for a new place to downsize and save money. SpareFoot essentially came to my rescue in more ways than one!',
		reviewerInitials: 'RY',
		link: 'https://www.trustpilot.com/reviews/5bf444779d3780035820669e',
		customerName: 'Ron Yoak',
		date: 'Nov 20, 2018',
	},
];

export const DirtBikeContent = {
	type: 'Dirt Bike',
	reviews: reviews,
	sections: [
		{
			title: 'Dirt Bike Hero',
			background: 'default',
			modules: [
				{
					type: 'hero',
					id: 'dirt-bike-hero',
					props: {
						content: {
							title: 'Find Affordable Dirt Bike Self Storage',
							description:
								"Your dirt bike shouldn't live crammed in a corner collecting cobwebs and scratches. Between muddy gear taking over your entryway, tools scattered across workbenches, and that constant worry about theft or weather damage, storing a dirt bike at home creates more stress than it solves.\n\nHere's the thing: dirt bikes need more than just a parking spot. They need proper protection from moisture, security from theft, and space for all the maintenance gear that keeps them running strong.\n\nThat's where professional dirt bike storage steps in – giving you back your garage while keeping your ride in peak condition.\n\n **SelfStorage.com** helps you find storage facilities designed for riders who want convenience, security, and peace of mind.",
							image: dirtBikeHero,
							CTAtext: 'Find Dirt Bike Self Storage',
						},
					},
				},
			],
		},
		{
			title: 'Size Comparison Section',
			noPadding: true,
			modules: [
				{
					type: 'sizeComparisonSection',
					props: {
						content: {
							title: 'Storage Unit Sizes for Dirt Bikes',
							image: dirtbike,
							contentdec:
								'**Hidden Cost**:\n\nSetup fees typically run $25-50 one-time.\n\nInsurance options add $15-30 monthly but provide valuable protection.\n\nClimate control adds on a premium, typically 35% to 50% more than standard units.\n\nSome facilities charge for electrical usage if you use battery tenders.',
							contentAlign: 'text-left',
						},

						storageData: [
							{
								size: '5x10',
								desc: [
									'**What fits**: One dirt bike with basic gear\n\n**Good for**: Single bike, helmet, boots, basic tools, cleaning supplies\n\n**Monthly cost** :$60-80\n\n**Best for**: Occasional riders, budget-conscious storage',
								],
							},
							{
								size: '10x10',
								desc: [
									'**What fits**: 2-3 dirt bikes or one bike with extensive gear\n\n**Good for**: Multiple bikes, spare parts, maintenance tools, seasonal equipment supplies\n\n**Monthly cost** :$90-120\n\n**Best for**: Regular riders, families with multiple bikes',
								],
							},
							{
								size: '10x15',
								desc: [
									'**What fits**: 3-4 dirt bikes or bikes with trailer and equipment\n\n**Good for**: Large collections, spare engines, transport gear, full workshop setup\n\n**Monthly cost** :$130-180\n\n**Best for**: Serious collectors, competitive riders, dealers',
								],
							},
						],
					},
				},
			],
		},
		{
			title: 'Content section',
			background: 'blueWithMap',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Winter-Ready Dirt Bike Storage: Your Seasonal Checklist',
						contentType: 'row-type',
						theme: 'dark',
						content: [
							"Proper winter prep prevents expensive spring surprises and keeps your dirt bike ready for next season's adventures.\n\n ### Week Before Storage\n\n - Schedule thorough wash and inspection\n\n - Order fresh oil, filter, and spark plug\n\n - Pick up fuel stabilizer and battery tender\n\n - Gather storage supplies (cover, moisture absorbers)\n\n ### Storage Day\n\n - Complete deep cleaning (pay extra attention to air filter area)\n\n - Fill fuel tank and add stabilizer per instructions\n\n - Remove battery and set up indoor charging station\n\n - Inflate tires to maximum recommended pressure\n\n - Apply fresh chain lube and protective spray on metal parts\n\n ### Monthly Check-ins\n\n - Battery maintenance and charge monitoring\n\n - Visual inspection for any moisture issues\n\n - Check tire pressure and adjust if needed\n\n - Spring Revival\n\n - Reinstall battery and test electrical systems\n\n - Check all fluids and top off as needed​\n\n - Inspect chain tension and lubrication\n\n - Test brakes and throttle response before first ride\n\n ### The Hidden Costs of Poor Storage\n\nStoring your dirt bike improperly doesn't just create clutter – it slowly drains your bank account through preventable damage and constant maintenance headaches.\n\n**Smart Storage Saves Money**: Professional storage prevents these issues while giving you organized space for maintenance, gear storage, and easy access when you're ready to ride.",
						],
					},
				},
			],
		},
		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			modules: [
				{
					type: 'reviews',
					props: {
						reviewsData: {
							headerTitle: 'Dirt Bike Self Storage: ',
							reviews,
						},
					},
				},
			],
		},
		{
			title: 'Faqs Section',
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'Dirt Bike Self Storage FAQs',
							faqs,
						},
					},
				},
			],
		},
		{
			title: 'featuredContent',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: {
							heading: 'Learn All About Dirt Bike Self Storage',
							data: {
								slug: 'dirt-bike-featured-content',
								unitCards: [
									{
										title: 'Vehicle Storage',
										date: 'November 4, 2025',
										image: motorcycle,
										content:
											'Quick Guide to Proper Motorcycle Winter Storage ',
										url: 'https://www.selfstorage.com/blog/guide-to-motorcycle-winter-storage/',
									},
									{
										title: 'Vehicle Storage',
										date: 'October 16, 2025',
										image: bicycle,
										content:
											'Everything You Need to Know About Storage Solutions for Bicycles',
										url: 'https://www.selfstorage.com/blog/how-to-store-bicycles-and-cycling-gear/',
									},
									{
										title: 'Storage Tips',
										date: 'October 16, 2025',
										image: shutterstock,
										content:
											'Sports Equipment Storage Guide: How to Create A Sports and Outdoor Gear Storage Room',
										url: 'https://www.selfstorage.com/blog/outdoor-gear-shed-storage-unit',
									},
									{
										title: 'Vehicle Storage',
										date: 'March 3, 2025',
										image: carStorage,
										content:
											'Choosing the Right Storage Unit for Your Vehicle',
										url: 'https://www.selfstorage.com/blog/car-storage-size-guide/',
									},
								],
							},
						},
					},
				},
			],
		},
		{
			title: 'Link to other PSPs',
			background: 'lighterBlue',
			modules: [
				{
					type: 'links',
					props: {
						heading: {
							headingName: 'More ways to store',
						},
						linksList: storageCategories.dirtBike,
					},
				},
			],
		},
		{
			title: 'nearby units',
			noPaddingBottom: true,
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'Dirt Bike',
					},
				},
			],
		},
	],
};
